import { type FunctionComponent, useEffect } from "react";
import { Main } from "@App/Layouts/Main";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { ContactUs } from "@App/Components/ContactUs";
import { CheckBadgeIcon } from "@heroicons/react/20/solid";
import { useScrollToTop } from "@App/hooks/useScrollToTop";
import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { useRedirectBack } from "@App/hooks/useRedirectBack";
import { ServiceFAQs } from "@App/Components/ServiceFAQs";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import type { State } from "@App/state";
import { ServiceCardInfo } from "@App/Pages/Partner/AyvensLight/ServiceCardInfo";
import { useTranslation } from "react-i18next";
import { ServiceIsVehicleReturn } from "@App/utils/conditionals/services";
import { OrganizationIsALD, OrganizationIsRepareo } from "@App/utils/conditionals/organization";
import { Role } from "@App/types/ayvens/role";
import { useVehicleSelector } from "@App/Pages/Partner/Ayvens/BusinessLogic/backToVehicleSelector";

export type SummaryProps = {
  embedded?: boolean;
};

export const Summary: FunctionComponent<SummaryProps> = ({ embedded = false }: SummaryProps) => {
  const { t } = useTranslation();
  useScrollToTop();
  const organization = useSelector((state: State) => state.orders.organization);
  const service = useSelector((state: State) => state.orders.service);
  const initialRole = useSelector((state: State) => state.auth.initialRole);

  const { resetTokenAndDriverData } = useVehicleSelector();

  // redirect back to start if parameters are missing
  const { redirectOnMissingParams } = useRedirectBack();
  const location = useLocation();
  useEffect(() => {
    redirectOnMissingParams(
      ["garage", "service", "place", "slot"],
      location.pathname.substring(0, location.pathname.indexOf("/werkstaetten")),
    );
  }, []);

  return (
    <>
      <Main>
        <div className="bg-white flex items-center justify-center flex-col max-w-3xl mx-auto">
          <div className={"mb-4 py-8"}>
            <CheckBadgeIcon className={"w-32 h-32 text-primary"} />
          </div>
          <div className={"mb-3"}>
            <div className="text-2xl mb-2">
              {service && ServiceIsVehicleReturn(service)
                ? t("summary.vehicleReturn.text")
                : t("summary.text")}
            </div>
            <div className={"mb-3"}>
              {OrganizationIsRepareo(organization)
                ? t("summary.contact-us-repareo")
                : service && ServiceIsVehicleReturn(service)
                  ? t("summary.vehicleReturn.contact-us-partner")
                  : t("summary.contact-us-partner")}
            </div>
            <div>
              <div className="flex gap-1 items-center mb-2">
                <PhoneIcon className={"text-primary mr-1 w-5 h-6"} />
                <div>
                  <a href={t("contact.phone.link")}>{t("contact.phone.title")}</a>
                  <div className="text-neutral-500 whitespace-pre-line">
                    {t("contact.reachability")}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 items-center mb-2">
                <EnvelopeIcon className={"text-primary mr-1 w-5 h-6"} />
                <a href={t("contact.email.link")}>{t("contact.email.title")}</a>
              </div>
            </div>
            {organization.slug === "leaseplan" && (
              <div className="mt-9">
                <ServiceCardInfo />
              </div>
            )}
            {OrganizationIsALD(organization) &&
              [Role.FLEET_MANAGER, Role.AGENT].includes(initialRole as Role) && (
                <button
                  type={"button"}
                  onClick={() => resetTokenAndDriverData(initialRole as Role)}
                >
                  <div
                    className={
                      "bg-primary w-full md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline"
                    }
                  >
                    {t("driverDataStep.backToVehicleSelection")}
                  </div>
                </button>
              )}
          </div>
        </div>
      </Main>
      {!embedded && (
        <section className="bg-neutral-100 pb-10">
          <TriangleDivider direction={"right"} background={"bg-white"} />
          <div className="container md:flex gap-10 mt-10">
            {!OrganizationIsALD(organization) && (
              <div className="w-full md:w-1/2">
                <ServiceFAQs />
              </div>
            )}
            <div className="w-full md:w-1/2">
              <ContactUs imageHidden={OrganizationIsALD(organization)} />
            </div>
          </div>
        </section>
      )}
    </>
  );
};
